import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from './redux/store';
import { Box } from '@mui/material';

// Layout Components
import Header from './components/Layout/Header';
import Sidebar from './components/Layout/Sidebar';
import Footer from './components/Layout/Footer';

// Auth Components
import SessionTimeoutWarning from './components/Auth/SessionTimeoutWarning';

// Common Components
import { ErrorBoundary, ToastContainer } from './components/Common';

// Public Pages
import LandingPage from './pages/Public/LandingPage';
import ForPartnersPage from './pages/Public/ForPartnersPage';
import ForCorporatesPage from './pages/Public/ForCorporatesPage';
import AboutPage from './pages/Public/AboutPage';
import ContactPage from './pages/Public/ContactPage';
import PrivacyPolicyPage from './pages/Public/PrivacyPolicyPage';
import TermsPage from './pages/Public/TermsPage';

// Auth Pages
import LoginSelection from './pages/Auth/LoginSelection';
import SignupSelection from './pages/Auth/SignupSelection';
import LoginPage from './pages/Auth/LoginPage';
import SignupPage from './pages/Auth/SignupPage';
import OTPVerificationPage from './pages/Auth/OTPVerificationPage';
import CorporateLoginPage from './pages/Auth/CorporateLoginPage';
import UserLoginPage from './pages/Auth/UserLoginPage';
import LoginHistoryPage from './pages/Auth/LoginHistoryPage';

// Dashboard Pages
import DashboardPage from './pages/Dashboard/DashboardPage';
import GymsListPage from './pages/Gyms/GymsListPage';
import CreateGymPage from './pages/Gyms/CreateGymPage';
import GymDetailsPage from './pages/Gyms/GymDetailsPage';
import EditGymPage from './pages/Gyms/EditGymPage';
import SlotsListPage from './pages/Slots/SlotsListPage';
import CreateSlotPage from './pages/Slots/CreateSlotPage';
import EditSlotPage from './pages/Slots/EditSlotPage';
import { EnhancedBookingsPage } from './pages/Bookings/EnhancedBookingsPage';
import { BookingDetailsFullPage } from './pages/Bookings/BookingDetailsFullPage';
import { RevenuePage } from './pages/Revenue/RevenuePage';
import { PayoutHistoryPage } from './pages/Revenue/PayoutHistoryPage';
import { BankDetailsPage } from './pages/Revenue/BankDetailsPage';
import { SlotsListPage as PricingSlotsListPage } from './pages/Pricing/SlotsListPage';
import { CreateSlotPage as PricingCreateSlotPage } from './pages/Pricing/CreateSlotPage';
import { BulkCreateSlotPage } from './pages/Pricing/BulkCreateSlotPage';
import { EditSlotPage as PricingEditSlotPage } from './pages/Pricing/EditSlotPage';
import { AnalyticsPage } from './pages/Analytics/AnalyticsPage';
import { EmployeesPage } from './pages/Corporate/EmployeesPage';
import { BulkUploadPage } from './pages/Corporate/BulkUploadPage';
import { CorporateAnalyticsPage } from './pages/Corporate/CorporateAnalyticsPage';
import { SettingsPage } from './pages/Settings/SettingsPage';
import { ProfilePage } from './pages/Settings/ProfilePage';
import { BankAccountsPage } from './pages/Settings/BankAccountsPage';
import { NotificationsPage } from './pages/Settings/NotificationsPage';
import { ApiKeysPage } from './pages/Settings/ApiKeysPage';
import { SecurityPage } from './pages/Settings/SecurityPage';
import { SupportPage } from './pages/Support/SupportPage';
import { FAQPage } from './pages/Support/FAQPage';
import { TicketsPage } from './pages/Support/TicketsPage';

// Investor Materials (Password Protected)
import InvestorMaterialsPage from './pages/Investor/InvestorMaterialsPage';

// Hard-redirect component for static HTML pages served by Express
const StaticHtmlPage: React.FC<{ url: string }> = ({ url }) => {
  React.useEffect(() => { window.location.replace(url); }, [url]);
  return null;
};

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, partner } = useSelector((state: RootState) => state.partner);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if partner needs OTP verification
  if (partner && !partner.is_partner_verified) {
    return <Navigate to="/verify-otp" replace />;
  }

  return <>{children}</>;
};

// Layout Wrapper for authenticated pages
const AuthenticatedLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', flexDirection: 'column' }}>
      <Header onMenuClick={handleDrawerToggle} />
      <Box sx={{ display: 'flex', flex: 1 }}>
        <Sidebar mobileOpen={mobileOpen} onMobileClose={handleDrawerToggle} />
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto',
          width: { xs: '100%', md: 'calc(100% - 250px)' },
          p: { xs: 2, sm: 3 }
        }}>
          {children}
          <Footer />
        </Box>
      </Box>
    </Box>
  );
};

const App: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated } = useSelector((state: RootState) => state.partner);
  const { logout } = require('./redux/partnerSlice');
  const [enableAutoLogout, setEnableAutoLogout] = React.useState(false);

  React.useEffect(() => {
    // Load session timeout setting from localStorage
    const saved = localStorage.getItem('enableSessionAutoLogout');
    if (saved !== null) {
      setEnableAutoLogout(JSON.parse(saved));
    }
  }, []);

  const handleSessionTimeout = () => {
    dispatch(logout());
  };

  return (
    <ErrorBoundary>
      <Router>
        {/* Toast Notification Container */}
        <ToastContainer />

        {/* Session Timeout Warning - shown to authenticated users */}
        {/* Auto-logout is DISABLED by default - can be enabled in settings */}
        {isAuthenticated && (
          <SessionTimeoutWarning 
            onLogout={handleSessionTimeout}
            enableAutoLogout={enableAutoLogout}
          />
        )}

        <Routes>
        {/* Public Static HTML pages — force hard browser navigation so Express serves them */}
        <Route path="/gym-survey" element={<StaticHtmlPage url="/gym-survey" />} />
        <Route path="/corporate-survey" element={<StaticHtmlPage url="/corporate-survey" />} />
        <Route path="/user-survey" element={<StaticHtmlPage url="/user-survey" />} />
        <Route path="/fitness-recommendation" element={<StaticHtmlPage url="/fitness-recommendation/" />} />
        <Route path="/fitness-recommendation/" element={<StaticHtmlPage url="/fitness-recommendation/" />} />

        {/* Public Landing Pages */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/partners" element={<ForPartnersPage />} />
        <Route path="/corporate" element={<ForCorporatesPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        
        {/* Investor Materials - Password Protected */}
        <Route path="/investor" element={<InvestorMaterialsPage />} />
        
        {/* Protected Content Routes - Redirect to Investor Portal */}
        <Route path="/pitch-deck" element={<Navigate to="/investor" replace />} />
        <Route path="/pitch-deck/*" element={<Navigate to="/investor" replace />} />
        <Route path="/investor-pitch" element={<Navigate to="/investor" replace />} />
        <Route path="/investor-pitch/*" element={<Navigate to="/investor" replace />} />
        <Route path="/investor-pitch-v2" element={<Navigate to="/investor" replace />} />
        <Route path="/investor-pitch-v2/*" element={<Navigate to="/investor" replace />} />
        <Route path="/investor-pitch-v3" element={<Navigate to="/investor" replace />} />
        <Route path="/investor-pitch-v3/*" element={<Navigate to="/investor" replace />} />
        <Route path="/pitch-script" element={<Navigate to="/investor" replace />} />
        <Route path="/pitch-script/*" element={<Navigate to="/investor" replace />} />
        <Route path="/ceo-strategy" element={<Navigate to="/investor" replace />} />
        <Route path="/ceo-strategy/*" element={<Navigate to="/investor" replace />} />
        <Route path="/survey-reports" element={<Navigate to="/investor" replace />} />
        <Route path="/survey-reports/*" element={<Navigate to="/investor" replace />} />

        {/* Auth Routes */}
        <Route path="/login" element={<LoginSelection />} />
        <Route path="/signup" element={<SignupSelection />} />
        <Route path="/partner-login" element={<LoginPage />} />
        <Route path="/partner-signup" element={<SignupPage />} />
        <Route path="/verify-otp" element={<OTPVerificationPage />} />
        <Route path="/corporate-login" element={<CorporateLoginPage />} />
        <Route path="/user-login" element={<UserLoginPage />} />
        <Route path="/login-history" element={<LoginHistoryPage />} />

        {/* Protected Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <DashboardPage />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/gyms"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <GymsListPage />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/gyms/create"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <CreateGymPage />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/gyms/:gymId"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <GymDetailsPage />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/gyms/:gymId/edit"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <EditGymPage />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/slots"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <SlotsListPage />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/slots/create"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <CreateSlotPage />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/slots/:slotId/edit"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <EditSlotPage />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <ProfilePage />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/bookings"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <EnhancedBookingsPage />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/bookings/:bookingId"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <BookingDetailsFullPage />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />

        {/* Revenue Routes */}
        <Route
          path="/revenue"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <RevenuePage />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/payouts"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <PayoutHistoryPage />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/bank-accounts"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <BankDetailsPage />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />

        {/* Pricing & Slot Management Routes */}
        <Route
          path="/pricing/gyms/:gymId/slots"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <PricingSlotsListPage />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/pricing/gyms/:gymId/slots/create"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <PricingCreateSlotPage />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/pricing/gyms/:gymId/slots/bulk"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <BulkCreateSlotPage />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/pricing/gyms/:gymId/slots/:slotId/edit"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <PricingEditSlotPage />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />

        {/* Analytics Routes */}
        <Route
          path="/analytics/gyms/:gymId"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <AnalyticsPage />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />

        {/* Corporate Routes */}
        <Route
          path="/corporate/employees"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <EmployeesPage />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/corporate/employees/bulk-upload"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <BulkUploadPage />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/corporate/analytics"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <CorporateAnalyticsPage />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />

        {/* Settings Routes */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <SettingsPage />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/profile"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <ProfilePage />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/bank-accounts"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <BankAccountsPage />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/notifications"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <NotificationsPage />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/api-keys"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <ApiKeysPage />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/security"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <SecurityPage />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />

        {/* Support Routes */}
        <Route
          path="/support"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <SupportPage />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/support/faqs"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <FAQPage />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/support/tickets"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <TicketsPage />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />

        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Router>
    </ErrorBoundary>
  );
};

export default App;
